<template>
  <div>
    <div class="buttonsForm">
      <button class="small-4" :class="{ 'button clear': formMode !== 1, 'button': formMode === 1 }"
        @click.prevent="defineMode(1)">{{ $t('SCHOOL_CONTACT_FORM.BUTTONS.CONTACT') }}</button>
      <button class="small-4" v-bind:disabled="!checkEnrollmentsPermission()"
        :class="{ 'button clear': formMode !== 2, 'button': formMode === 2, 'disabled': !checkEnrollmentsPermission() }"
        @click.prevent="hasSchoolContact() && defineMode(2)">{{ $t('SCHOOL_CONTACT_FORM.BUTTONS.ENROLLMENT') }}</button>
      <button class="small-4" v-bind:disabled="!checkEnrollmentsPermission()"
        :class="{ 'button clear': formMode !== 3, 'button': formMode === 3, 'disabled': !checkEnrollmentsPermission() }"
        @click.prevent="hasSchoolContact() && defineMode(3)">{{ $t('SCHOOL_CONTACT_FORM.BUTTONS.CONTRACT') }}</button>
    </div>

    <school-contact-form v-if="formMode === 1" v-bind="$attrs" @success="onSuccess" @cancel="onCancel"
      :selectedSchoolLabelsList="selectedSchoolLabels" />
    
    <div v-if="contractEditStatus && contractEditStatus.has_pending_contract" class="sync-section">
      <div class="alert-message warning margin-bottom-1">
        <div class="alert-content">
          <div class="alert-header">
            <fluent-icon icon="info" size="18" class="alert-icon" />
            <span class="alert-title">📋 Contrato em Análise</span>
          </div>
          <div class="alert-text">
            Este contrato está sendo analisado pelo responsável.<br>
            As edições ficam temporariamente bloqueadas para preservar a integridade dos dados.
            <br><br>
            <strong>💡 Dica:</strong> Se precisar fazer alterações urgentes, peça para o responsável rejeitar o contrato atual.
            <br /><br />
            <a
              v-if="contractViewLink"
              :href="contractViewLink"
              target="_blank"
              rel="noopener noreferrer"
              class="contract-link"
            >
              📄 Clique aqui para visualizar o contrato
            </a>
          </div>
        </div>
        <div class="alert-actions">
          <button
            @click="syncContractManually"
            :disabled="syncing"
            :class="['button', 'small', 'clear', 'secondary', 'sync-button', { 'loading': syncing }]"
            :title="syncing ? 'Verificando status...' : 'Verificar se houve mudanças no status'"
          >
            <fluent-icon :icon="syncing ? 'repeat' : 'arrow-clockwise'" size="16" />
            {{ syncing ? '⏳ Verificando...' : 'Verificar Status' }}
          </button>
        </div>
      </div>
    </div>

    <school-enrollments-form v-if="formMode === 2" v-bind="$attrs" @cancel="onCancel"
      :selectedSchoolLabels="selectedSchoolLabels" :contractEditStatus="contractEditStatus"
      @enrollment-selected="checkContractEditStatus" />
    <school-contract-form v-if="formMode === 3" v-bind="$attrs" @cancel="onCancel"
      :selectedSchoolLabels="selectedSchoolLabels" :contractEditStatus="contractEditStatus"
      @enrollment-selected="checkContractEditStatus" />

  </div>
</template>

<script>
import VueMask from 'v-mask';
import Vue from 'vue';
import SchoolContactForm from './SchoolContactForm.vue';
import SchoolContractForm from './SchoolContractForm.vue';
import SchoolEnrollmentsForm from './SchoolEnrollmentsForm.vue';
import DigitalAcceptanceContractsAPI from 'dashboard/api/digitalAcceptanceContracts';
Vue.use(VueMask);

export default {
  components: {
    SchoolContactForm,
    SchoolEnrollmentsForm,
    SchoolContractForm
  },

  data() {
    return {
      formMode: 1,
      contractEditStatus: null,
      syncing: false,
    }
  },

  watch: {
    contactId() {
      this.formMode = 1;
    },
    formMode(newMode) {
      if (newMode === 2 || newMode === 3) {
        this.$nextTick(() => {
          this.resetContractEditStatus();
        });
      }
    }
  },

  computed: {
    contact() {
      return this.$attrs.contact
    },
    contactId() {
      return this.contact?.id
    },
    selectedSchoolLabels() {
      return this.contact?.school_contact?.school_label_list;
    },
    contractViewLink() {
      return this.contractEditStatus?.contract_view_url || null;
    },
  },

  methods: {
    defineMode(value) {
      this.formMode = value;
    },
    onCancel() {
      this.$emit('cancel');
    },
    async onSuccess() {
      if (this.contractEditStatus?.can_edit && this.formMode === 'contrato') {
        await this.regenerateContractAfterEdit();
      }

      this.$emit('success');
    },
    hasSchoolContact() {
      return !!this.$attrs.contact?.school_contact;
    },
    checkAllowedSchoolTagsArePresent() {
      if (this.selectedSchoolLabels?.length > 0) {
        return [
          '09_finalização_de_dados',
          '10_assinatura_de_contrato',
          '11_matrícula',
          '12_contrato_recusado',
          '13_matrícula_manual',
          '14_contrato_parcial',
        ].some(label => this.selectedSchoolLabels.includes(label));
      }
      return false
    },
    checkEnrollmentsPermission() {
      return this.checkAllowedSchoolTagsArePresent() && this.hasSchoolContact()
    },
    async checkContractEditStatus(enrollmentId) {
      if (!enrollmentId || !this.contactId) return;

      try {
        const api = DigitalAcceptanceContractsAPI.forContact(this.contactId);
        const response = await api.checkEnrollmentEditStatus(enrollmentId);

        this.contractEditStatus = response.data;
      } catch (error) {
        this.contractEditStatus = null;
      }
    },

    async syncContractManually() {
      if (!this.contractEditStatus?.blocking_contract_id) return;

      this.syncing = true;
      try {
        const api = DigitalAcceptanceContractsAPI.forContact(this.contactId);
        const response = await api.syncContractStatus(this.contractEditStatus.blocking_contract_id);

        if (response.data.synchronized) {
          this.$emit('success', `Status atualizado: ${response.data.old_status} → ${response.data.new_status}`);

          const enrollmentId = this.contractEditStatus?.blocking_contract?.school_enrollment_id;
          if (enrollmentId) {
            await this.checkContractEditStatus(enrollmentId);
          }
        } else {
          this.$emit('info', 'Status já está atualizado');
        }
      } catch (error) {
        this.$emit('error', 'Erro ao sincronizar contrato');
      } finally {
        this.syncing = false;
      }
    },

    async regenerateContractAfterEdit() {
      try {
        const api = DigitalAcceptanceContractsAPI.forContact(this.contactId);
        const enrollmentId = this.schoolEnrollments?.[this.selectedSchoolEnrollmentIndex]?.id;

        if (!enrollmentId) {
          return;
        }

        await api.regenerateContract(enrollmentId);

        this.$emit('success', 'Contrato regenerado com sucesso! Aguarde o novo link na conversa.');

        await new Promise(resolve => setTimeout(resolve, 3000));

        await this.checkContractEditStatus(enrollmentId);

        if (this.contractEditStatus?.can_edit) {
          this.contractEditStatus.can_edit = false;
          this.contractEditStatus.has_pending_contract = true;
          this.contractEditStatus.message = 'Novo contrato gerado. Aguarde aprovação/rejeição do responsável.';
        }

      } catch (error) {
        this.$emit('error', 'Erro ao regenerar contrato');
      }
    },

    resetContractEditStatus() {
      // Verification of pending contracts will only be done when a specific registration is selected
      this.contractEditStatus = null;
    }
  },

  async mounted() {
    if (this.contact) {
      await this.$store.dispatch('schoolEnrollments/get', this.contact.id);

      this.resetContractEditStatus();
    }
  },
}
</script>

<style scoped lang="scss">
.buttonsForm {
  display: flex;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 5px;
  border-top: 1px solid;
  border-color: rgb(184, 182, 182);
  padding: 10px;
}

.button {
  display: flex;
  justify-content: center;
  text-align: center;
}

.sync-section {
  margin: 12px 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  clear: both;
}

.alert-message {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fefce8 0%, #fef3cd 100%);
  border: 1px solid #f59e0b;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
  line-height: 1.6;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.15);
    transform: translateY(-1px);
  }

  &.warning {
    background: linear-gradient(135deg, #fefce8 0%, #fef3cd 100%);
    border-color: #f59e0b;
    color: #92400e;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
  }
}

.alert-content {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.alert-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.alert-icon {
  color: #f59e0b;
  flex-shrink: 0;
}

.alert-title {
  font-weight: 700;
  font-size: 16px;
  color: #b45309;
  letter-spacing: -0.025em;
}

.alert-text {
  font-size: 13px;
  line-height: 1.5;
  color: #a16207;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  margin-top: 4px;
  max-width: 100%;

  br {
    line-height: 0.8;
  }

  .contract-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1f73e8;
    text-decoration: none !important;
    font-weight: 600;
    margin-top: 8px;
    transition: color 0.2s ease;

    &:hover {
      color: #1765cc;
      text-decoration: none !important;
    }

    &:visited {
      color: #1f73e8;
      text-decoration: none !important;
    }
  }
}

.alert-actions {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  align-items: flex-start;
  padding-top: 4px;
}

.sync-button {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 12px 18px;
  white-space: nowrap;
  border-radius: 8px;
  border: 1px solid #1f73e8;
  background: #1f73e8;
  color: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(31, 115, 232, 0.2);

  &:hover:not(:disabled) {
    background: #1765cc;
    border-color: #1765cc;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(31, 115, 232, 0.3);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    background: #1557b0;
    border-color: #1557b0;
    box-shadow: 0 2px 6px rgba(31, 115, 232, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: #1f73e8;
    border-color: #1f73e8;
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(31, 115, 232, 0.1);

    &:hover {
      transform: none;
      background: #1f73e8;
      border-color: #1f73e8;
      color: #ffffff;
    }
  }

  .fluent-icon {
    transition: transform 0.2s ease;
    color: #ffffff;
  }

  &:hover:not(:disabled) .fluent-icon {
    transform: rotate(180deg);
  }

  &.loading .fluent-icon {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.margin-bottom-1 {
  margin-bottom: 8px;
}

@media (max-width: 1200px) {
  .alert-message {
    padding: 18px;
    gap: 14px;
  }
  
  .alert-text {
    font-size: 13px;
  }
}

@media (max-width: 767px) {
  .sync-section {
    margin: 8px 0;
    position: relative;
    z-index: 1;
  }

  .alert-message {
    padding: 14px;
    gap: 10px;
    margin: 0;
    border-radius: 8px;
  }

  .alert-title {
    font-size: 14px;
    line-height: 1.3;
  }

  .alert-text {
    font-size: 12px;
    line-height: 1.4;

    br + br {
      display: none;
    }

    strong {
      display: block;
      margin-top: 6px;
      font-size: 11px;
    }
  }

  .sync-button {
    font-size: 11px;
    padding: 8px 12px;
    gap: 4px;
    width: 100%;
    justify-content: center;
    min-height: 36px;
  }

  .alert-actions {
    justify-content: center;
    margin-top: 6px;
  }
}

@media (max-width: 480px) {
  .alert-message {
    padding: 14px;
    gap: 10px;
  }
  
  .alert-title {
    font-size: 14px;
  }
  
  .alert-text {
    font-size: 12px;
    line-height: 1.5;
  }
  
  .sync-button {
    font-size: 11px;
    padding: 8px 12px;
  }
}
</style>
